// Chain reveal helpers.
//
// The store latches CHAIN_REVEALED once the reveal window is reached (or the
// video ends); the chain surfaces read that latch to decide visibility. The
// end-of-video countdown itself is a fixed wall-clock timer owned by
// NextVideoBlock, not derived from the remaining time here.
import { chainStore } from "@/stores/chain/store";
import appConfig from "@/modules/config";
import { isLastMethodStep } from "@/modules/methodStep";
import { valueNames } from "@/modules/Constants";
import type ApplicationData from "@/modules/ApplicationData";
import type ExperienceData from "@/modules/ExperienceData";
import type ShopWindowConfig from "@/plugin/ShopWindowConfig";

// Is the chain UI revealed? Latched by the store (CHAIN_REVEALED) once the
// reveal window is reached or the video ends, and it stays revealed for the rest
// of the experience — so scrubbing back out of the window never hides the chain
// (a consistent end-state once shown). `VIDEO_ENDED` is a belt-and-braces
// backstop for a short video that ends before the latch is set.
export function isChainRevealed(applicationData: ApplicationData): boolean {
  return (
    (applicationData.getValue(valueNames.CHAIN_REVEALED, false) as boolean) ||
    (applicationData.getValue(valueNames.VIDEO_ENDED, false) as boolean)
  );
}

// Is the chain UI actually active — revealed AND the store holds a ready,
// non-empty chain? The `shop-window--chain-end` layout (last-step content + Up
// Next sharing one top-aligned scroll region) must apply ONLY when there are
// recommendations to show; with an empty chain the shop window keeps its normal
// centered layout. Single source of truth for "is there a chain to show",
// shared by the shop-window root class and UpNextList's render gate.
export function isChainActive(applicationData: ApplicationData): boolean {
  return (
    isChainRevealed(applicationData) &&
    chainStore.getStatus() === "ready" &&
    (chainStore.getChain() || []).length > 0
  );
}

// Should the Method step anchor to the top (chain-end layout) to make room for
// the chain surface? True once the chain is ready and EITHER the recs list is
// revealed (T−15s) OR we're on the last method step — where the "Up Next" prompt
// appears first, before T−15s. Used in portrait so the step rises as soon as the
// prompt shows, not only at the reveal; landscape keeps `isChainActive`.
export function isChainAnchoredOnMethodStep(
  applicationData: ApplicationData,
  methodConfig: ShopWindowConfig | null | undefined,
  experienceData: ExperienceData | null | undefined,
): boolean {
  const ready =
    chainStore.getStatus() === "ready" &&
    (chainStore.getChain() || []).length > 0;
  if (!ready) {
    return false;
  }
  return (
    isChainRevealed(applicationData) ||
    isLastMethodStep(
      methodConfig,
      experienceData,
      appConfig.getVisibleMethodSteps(),
    )
  );
}
